import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SectionTitle from '../components/common/SectionTitle';
import Button from '../components/common/Button';
import { fadeInUp } from '../utils/animations';

const Commercial = () => {
  const navigate = useNavigate();

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <section className="py-20 hero-gradient">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif text-dark mb-6">
              Commercial <span className="text-primary-brown">Excellence</span>
            </h1>
            <p className="text-xl text-dark-light leading-relaxed">
              Professional finishing solutions designed for commercial spaces that inspire.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Service 1: Interior Finishing & Decorative Coatings */}
      <section id="paint-coatings" className="py-20 bg-white scroll-mt-24">
        <div className="container mx-auto px-4">
          <SectionTitle
            title="Interior Finishing & Decorative Coatings"
            subtitle="Service 01"
            align="center"
          />

          <motion.div
            className="max-w-6xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-lg text-dark-light leading-relaxed text-center mb-16">
              Focused on creating clean, inviting, and well-balanced interiors that enhance brand experience and customer interaction.
            </p>

            {/* Grid Layout with Alternating Design */}
            <div className="space-y-12">
              {/* First Subcategory - Image Left */}
              <motion.div
                className="grid md:grid-cols-2 gap-8 items-center"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
              >
                <div className="bg-accent-cream p-8 md:p-10 rounded-lg shadow-lg h-full flex flex-col justify-center">
                  <div className="w-16 h-16 bg-primary-brown rounded-full flex items-center justify-center mb-6">
                    <span className="text-3xl text-white">1</span>
                  </div>
                  <h4 className="text-2xl font-serif text-dark mb-4">Premium Wall Paint Applications</h4>
                  <p className="text-dark-light leading-relaxed">
                    Our premium paint application service focuses on achieving smooth, uniform, and long-lasting finishes for interior spaces. We select high-quality paints suited to the environment, ensuring clean coverage, consistent tone, and refined visual appeal. The result is a polished interior surface that elevates the space without unnecessary complexity.
                  </p>
                </div>
                <div className="bg-primary-beige h-64 md:h-96 rounded-lg shadow-lg overflow-hidden">
                  <img src="/images/Commercial Service 1 Sub 1.png" alt="Premium Wall Paint Applications" className="w-full h-full object-cover" />
                </div>
              </motion.div>

              {/* Second Subcategory - Image Right */}
              <motion.div
                className="grid md:grid-cols-2 gap-8 items-center"
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                <div className="bg-primary-tan h-64 md:h-96 rounded-lg shadow-lg md:order-1 overflow-hidden">
                  <img src="/images/Commercial Service 1 Sub 2.png" alt="Textured Finishes and Feature Walls" className="w-full h-full object-cover" />
                </div>
                <div className="bg-primary-beige p-8 md:p-10 rounded-lg shadow-lg h-full flex flex-col justify-center md:order-2">
                  <div className="w-16 h-16 bg-primary-brown rounded-full flex items-center justify-center mb-6">
                    <span className="text-3xl text-white">2</span>
                  </div>
                  <h4 className="text-2xl font-serif text-dark mb-4">Textured Finishes and Feature Walls</h4>
                  <p className="text-dark-light leading-relaxed">
                    Textured coatings and feature walls introduce depth, character, and visual focus to a room. From subtle texture to bold statement finishes, these surfaces are crafted to complement the overall design while adding a unique point of interest. Each finish is tailored to match the desired atmosphere, whether calm and minimal or warm and expressive.
                  </p>
                </div>
              </motion.div>

              {/* Third Subcategory - Full Width Card */}
              <motion.div
                className="bg-gradient-to-r from-accent-cream to-primary-beige p-10 rounded-lg shadow-xl"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
              >
                <div className="max-w-4xl mx-auto">
                  <div className="w-16 h-16 bg-primary-brown rounded-full flex items-center justify-center mb-6">
                    <span className="text-3xl text-white">3</span>
                  </div>
                  <h4 className="text-2xl font-serif text-dark mb-4">Color Consultation and Design Alignment</h4>
                  <p className="text-dark-light leading-relaxed text-lg">
                    Selecting the right color is essential for creating harmony within a space. Our consultation service helps clients identify shades and combinations that work with lighting, furnishings, and architectural elements. We guide the choice to ensure the final palette feels balanced, intentional, and aligned with the aesthetic identity of the space.
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Service 2: High-Durability Floor & Surface Treatments */}
      <section id="texture-finishes" className="py-20 bg-accent-cream scroll-mt-24">
        <div className="container mx-auto px-4">
          <SectionTitle
            title="High-Durability Floor & Surface Treatments"
            subtitle="Service 02"
            align="center"
          />

          <motion.div
            className="max-w-6xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-lg text-dark-light leading-relaxed text-center mb-16">
              Ideal for high-footfall environments such as retail spaces, offices, hotels, clinics, and showrooms where durability and appearance need to work together.
            </p>

            {/* Three Column Card Layout */}
            <div className="grid md:grid-cols-3 gap-8">
              <motion.div
                className="bg-white p-8 rounded-lg shadow-xl hover:shadow-2xl transition-shadow duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
              >
                <div className="w-14 h-14 bg-primary-brown rounded-lg flex items-center justify-center mb-6">
                  <span className="text-2xl text-white">1</span>
                </div>
                <h4 className="text-xl font-serif text-dark mb-4">Seamless Commercial Floor Coatings</h4>
                <p className="text-dark-light leading-relaxed">
                  Seamless commercial floor coatings create smooth, joint-free surfaces that are easy to clean and maintain. These systems help prevent dust buildup, improve hygiene, and provide a clean, uninterrupted visual appearance. They are ideal for showrooms, offices, hospitality spaces, and retail environments where both performance and presentation matter.
                </p>
              </motion.div>

              <motion.div
                className="bg-white p-8 rounded-lg shadow-xl hover:shadow-2xl transition-shadow duration-300 md:transform md:scale-105"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                <div className="w-14 h-14 bg-primary-brown rounded-lg flex items-center justify-center mb-6">
                  <span className="text-2xl text-white">2</span>
                </div>
                <h4 className="text-xl font-serif text-dark mb-4">Protective Wear-Resistant Surfaces</h4>
                <p className="text-dark-light leading-relaxed">
                  Wear-resistant surfaces are engineered to withstand continuous foot traffic, furniture movement, and daily operational use. These coatings protect the flooring from scratches, scuffs, and surface deterioration, ensuring that commercial spaces retain a neat and professional look over time.
                </p>
              </motion.div>

              <motion.div
                className="bg-white p-8 rounded-lg shadow-xl hover:shadow-2xl transition-shadow duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
              >
                <div className="w-14 h-14 bg-primary-brown rounded-lg flex items-center justify-center mb-6">
                  <span className="text-2xl text-white">3</span>
                </div>
                <h4 className="text-xl font-serif text-dark mb-4">Maintenance-Friendly Finishes</h4>
                <p className="text-dark-light leading-relaxed">
                  Maintenance-friendly finishes simplify daily cleaning and long-term upkeep. By using coatings that resist stains, spills, and surface damage, these systems reduce maintenance efforts and extend the life of the floor. This ensures spaces stay functional, clean, and visually consistent with minimal ongoing care.
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Service 3: Exterior Protection & Aesthetic Enhancement */}
      <section id="interior-exterior" className="py-20 bg-white scroll-mt-24">
        <div className="container mx-auto px-4">
          <SectionTitle
            title="Exterior Protection & Aesthetic Enhancement"
            subtitle="Service 03"
            align="center"
          />

          <motion.div
            className="max-w-6xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-lg text-dark-light leading-relaxed text-center mb-16">
              Developed to maintain colour consistency, protect building surfaces, and preserve the architectural character over time.
            </p>

            {/* Staggered Card Layout */}
            <div className="space-y-8">
              <motion.div
                className="grid md:grid-cols-3 gap-8"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
              >
                <div className="md:col-span-2 bg-accent-cream p-10 rounded-lg shadow-lg">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary-brown rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-xl text-white">1</span>
                    </div>
                    <div>
                      <h4 className="text-2xl font-serif text-dark mb-4">Weather-Resistant Exterior Coating Systems</h4>
                      <p className="text-dark-light leading-relaxed">
                        Weather-resistant exterior coatings are designed to protect building surfaces from sun exposure, rainfall, humidity, and everyday environmental stress. These coatings preserve color, prevent surface degradation, and maintain a clean, uniform appearance over time. Ideal for both residential and commercial façades that require long-term performance outdoors.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-primary-beige h-64 md:h-80 rounded-lg shadow-lg overflow-hidden">
                  <img src="/images/Commercial Service 3 Sub 1.png" alt="Weather-Resistant Exterior Coating Systems" className="w-full h-full object-fill" />
                </div>
              </motion.div>

              <motion.div
                className="grid md:grid-cols-3 gap-8"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                <div className="bg-primary-tan h-64 md:h-80 rounded-lg shadow-lg overflow-hidden">
                  <img src="/images/Commercial Service 3 Sub 2.png" alt="Façade Refresh & Restoration" className="w-full h-full object-fill" />
                </div>
                <div className="md:col-span-2 bg-primary-beige p-10 rounded-lg shadow-lg">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary-brown rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-xl text-white">2</span>
                    </div>
                    <div>
                      <h4 className="text-2xl font-serif text-dark mb-4">Façade Refresh & Restoration</h4>
                      <p className="text-dark-light leading-relaxed">
                        Façade refresh and restoration services help revive worn, faded, or aged exterior surfaces. This includes surface cleaning, repair, preparation, and re-coating to restore the structure's original character and visual appeal. The result is a renewed, well-presented exterior that enhances the overall architecture without altering its identity.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                className="bg-gradient-to-r from-primary-tan to-accent-cream p-10 rounded-lg shadow-xl"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
              >
                <div className="max-w-4xl mx-auto">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary-brown rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-xl text-white">3</span>
                    </div>
                    <div>
                      <h4 className="text-2xl font-serif text-dark mb-4">UV & Moisture-Protective Layers</h4>
                      <p className="text-dark-light leading-relaxed text-lg">
                        UV and moisture-protective coatings form a durable barrier that prevents discoloration, cracking, and weather-related damage. They protect against sunlight, heat, rain, and humidity to keep exterior surfaces stable and structurally sound. This ensures long-lasting durability and reduced maintenance requirements over time.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-brown text-white">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-3xl mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-serif mb-6">
              Elevate Your Commercial Space
            </h2>
            <p className="text-xl mb-8 text-primary-beige">
              Let's discuss your project and create something exceptional.
            </p>
            <Button variant="secondary" size="large" onClick={() => navigate('/contact')}>
              Schedule Consultation
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Commercial;
